import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { SdTestAppBenchClient, type FineTuneCalibrationSnapshot } from "@/lib/sdtestapp/sdTestAppBenchClient";

export function BenchFineTuneCalibrationModal({
  open,
  port,
  onClose,
  onComplete,
}: {
  open: boolean;
  port: string;
  onClose: () => void;
  onComplete: () => void;
}): JSX.Element | null {
  const [snapshot, setSnapshot] = useState<FineTuneCalibrationSnapshot | null>(null);
  const [measuredAmps, setMeasuredAmps] = useState("");
  const [playSeconds, setPlaySeconds] = useState("2");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    const client = new SdTestAppBenchClient(port);
    void client
      .openFineTuneCalibration()
      .then((next) => {
        setSnapshot(next);
        setMeasuredAmps(next.storedMeasuredAmps == null ? "" : String(next.storedMeasuredAmps));
        setPlaySeconds(String(next.defaultPlaySeconds ?? 2));
        setMessage(null);
        setError(null);
      })
      .catch((loadError) => setError(loadError instanceof Error ? loadError.message : String(loadError)));
  }, [open, port]);

  if (!open) return null;

  const client = new SdTestAppBenchClient(port);

  const run = async (action: () => Promise<void>) => {
    setPending(true);
    setError(null);
    try {
      await action();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : String(actionError));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded border bg-background p-4 shadow-lg">
        <h3 className="text-lg font-semibold">Fine-tune calibration</h3>
        {snapshot ? (
          <p className="mt-2 text-sm text-muted-foreground">{snapshot.description}</p>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">Opening fine-tune session…</p>
        )}

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="fine-tune-amps">Measured amps</Label>
            <Input
              id="fine-tune-amps"
              className="mt-1 font-mono"
              value={measuredAmps}
              onChange={(event) => setMeasuredAmps(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="fine-tune-play-seconds">Preview seconds</Label>
            <Input
              id="fine-tune-play-seconds"
              className="mt-1 font-mono"
              value={playSeconds}
              onChange={(event) => setPlaySeconds(event.target.value)}
            />
          </div>
        </div>

        {snapshot?.storedOutputPercent != null ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Stored output percent for point {snapshot.currentPoint}: {snapshot.storedOutputPercent.toFixed(6)}%
          </p>
        ) : null}

        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={pending || !snapshot}
            onClick={() =>
              void run(async () => {
                if (!snapshot) return;
                await client.previewFineTuneCalibration({
                  pointIndex: snapshot.currentPoint,
                  playSeconds: Number.parseFloat(playSeconds),
                });
              })
            }
          >
            Replay tone
          </Button>
          <Button
            type="button"
            disabled={pending || !snapshot || measuredAmps.trim() === ""}
            onClick={() =>
              void run(async () => {
                const next = await client.confirmFineTuneCalibration({
                  measuredAmps: Number.parseFloat(measuredAmps),
                  playSeconds: Number.parseFloat(playSeconds),
                });
                setSnapshot(next);
                setMessage(next.message ?? null);
                if (next.complete) {
                  onComplete();
                  onClose();
                  return;
                }
                setMeasuredAmps(next.storedMeasuredAmps == null ? "" : String(next.storedMeasuredAmps));
              })
            }
          >
            Confirm point {snapshot?.currentPoint ?? "—"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={pending}
            onClick={() =>
              void run(async () => {
                await client.stopFineTuneCalibration();
                onClose();
              })
            }
          >
            Stop
          </Button>
        </div>

        {pending ? (
          <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Working…
          </p>
        ) : null}
        {message ? <p className="mt-2 text-sm text-amber-800">{message}</p> : null}
        {error ? <p className="mt-2 text-sm text-destructive">{error}</p> : null}
      </div>
    </div>
  );
}
